// Docked tab bar: Today · Progress · Pro · Profile.
//
// Docked, not floating. The old pill sat inset with rounded ends over a
// translucent fill, so page text slid *under* it and stayed legible through
// the material — which reads as a rendering fault rather than as glass. A bar
// spanning the full width on a near-opaque base tells you where content ends.
//
// Selection is a piece of liquid glass you can MOVE. Tap a tab and the glass
// glides to it; put a finger anywhere on the bar and drag, and the glass
// tracks the finger like a physical slab — brightening while held, stretching
// slightly with the pull, switching the app live as it crosses each column.
// The page swipe tugs it too, so bar and content read as one connected pager.
//
// The glass is positioned imperatively, from exactly one owner at a time
// (settle effect, dock drag, or page-swipe lean) — an element animated from
// two owners at once is how indicators fight themselves. Two mechanisms are
// banned outright, because the first travelling capsule wiped neighbouring
// tab titles with them: `will-change` (a permanently promoted layer paints
// over siblings in WKWebView) and easings that overshoot the target column.
// Direct finger-tracking is exempt from the overshoot rule by nature: the
// finger IS the animation, and the clamp stops it at the bar's walls.
import { useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Flame, TrendingUp, UserRound } from "lucide-react";
import type { ComponentType } from "react";

/* The Pro tab is gone (owner, 2026-08-14): four tabs, and caloly Premium
   lives at the top of the Profile page — the /pro page is reached from
   there. The CalMark tab icon went with it. */
/** Cal AI's tab mark: the same character, curious mouth, with a small
 *  sparkle — the assistant, not a generic chat glyph (owner decision). */
function CalAIMark({ size = 21, className = "" }: { size?: number | string; strokeWidth?: number | string; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" aria-hidden="true" className={className}>
      <defs>
        <linearGradient id="calai-tab" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b6f24a" /><stop offset="100%" stopColor="#18b6d8" />
        </linearGradient>
      </defs>
      <path d="M41 15c-4-6-1-12 6-15-2 6 6 8 4 15-1.8 5.5-8.5 5.5-10 0Z" fill="url(#calai-tab)" opacity="0.9" />
      <path d="M40 10C56 10 71 19 72 37c1 15-8 31-24 35C31 76 11 70 8 52 5 35 20 10 40 10Z" fill="url(#calai-tab)" opacity="0.94" />
      <ellipse cx="36" cy="52" rx="18" ry="13" fill="#ffffff" opacity="0.16" />
      <circle cx="32" cy="36" r="4" fill="#0a0a0a" /><circle cx="50" cy="36" r="4" fill="#0a0a0a" />
      <ellipse cx="41" cy="50" rx="4.5" ry="5" fill="#0a0a0a" />
      {/* the sparkle: thinking, attached to the character */}
      <path d="M67 6 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3 Z" fill="#ffffff" opacity="0.95" />
    </svg>
  );
}
import { useT } from "../lib/i18n";
import { hapticTap } from "../lib/haptics";

type TabIcon = ComponentType<{ size?: number | string; strokeWidth?: number | string; className?: string; "aria-hidden"?: React.AriaAttributes["aria-hidden"] }>;
interface TabSpec { to: string; key: string; Icon: TabIcon }
const TABS: TabSpec[] = [
  { to: "/",         key: "tab.today",    Icon: Flame },
  { to: "/progress", key: "tab.progress", Icon: TrendingUp },
  { to: "/cal",      key: "tab.calai",    Icon: CalAIMark },
  { to: "/more",     key: "tab.more",     Icon: UserRound },
];

export function BottomTabs() {
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Which tab owns the current route (prefix match covers nested pages like
  // /more/preferences). -1 on a non-tab route hides the glass.
  const activeIndex = TABS.findIndex((tab) =>
    tab.to === "/" ? pathname === "/" : pathname === tab.to || pathname.startsWith(tab.to + "/"),
  );

  const rowRef = useRef<HTMLDivElement>(null);
  const glideRef = useRef<HTMLSpanElement>(null);
  const draggingRef = useRef(false);
  const activeRef = useRef(activeIndex);
  activeRef.current = activeIndex;
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const mountedRef = useRef(false);

  // Settle the glass onto the active column whenever the route changes and no
  // finger owns it. The first paint places it without animating — an app that
  // opens with its indicator sliding in from a corner looks broken.
  useEffect(() => {
    const glide = glideRef.current;
    if (!glide || draggingRef.current) return;
    const settle = () => {
      glide.style.transform = `translateX(${Math.max(0, activeIndex) * 100}%) scaleX(1)`;
      glide.style.opacity = activeIndex < 0 ? "0" : "1";
    };
    if (!mountedRef.current) {
      mountedRef.current = true;
      glide.style.transition = "none";
      settle();
      requestAnimationFrame(() => { glide.style.transition = ""; });
    } else {
      glide.style.transition = "";
      settle();
    }
  }, [activeIndex]);

  // Drag-to-choose on the bar itself: pick the slab up, slide it, and the app
  // follows underneath. React's root touch listeners are passive, so the
  // preventDefault this needs has to come from listeners we attach ourselves.
  useEffect(() => {
    const row = rowRef.current, glide = glideRef.current;
    if (!row || !glide) return;
    let startX = 0, grabPx = 0, lastCol = -1, tracking = false, engaged = false;

    const colW = () => row.clientWidth / TABS.length;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      tracking = true;
      engaged = false;
      startX = e.touches[0].clientX;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking) return;
      if (!engaged) {
        if (Math.abs(e.touches[0].clientX - startX) < 10) return; // a tap stays a tap
        engaged = true;
        draggingRef.current = true;
        glide.classList.add("tab-glide-held");
        // Pick the slab up exactly where it visually IS — which may be
        // mid-glide, since the pointer-down tap has already selected the tab
        // under the finger and sent the glass moving. Reading the live
        // transform (and re-anchoring the finger) means engaging a drag never
        // teleports the glass anywhere.
        const m = new DOMMatrixReadOnly(getComputedStyle(glide).transform);
        grabPx = Number.isFinite(m.m41) ? m.m41 : Math.max(0, activeRef.current) * colW();
        startX = e.touches[0].clientX;
        lastCol = Math.round(grabPx / colW());
      }
      e.preventDefault();
      const dx = e.touches[0].clientX - startX;
      const w = colW();
      // Clamped to the bar: the slab stops at the walls instead of leaving.
      const px = Math.min((TABS.length - 1) * w, Math.max(0, grabPx + dx));
      // The liquid in the glass: a slight stretch with the pull, capped far
      // below anything that could reach a neighbouring label.
      const stretch = 1 + Math.min(0.07, Math.abs(dx) / 1200);
      glide.style.transition = "none";
      glide.style.transform = `translateX(${px}px) scaleX(${stretch})`;
      // Crossing a column switches the app live under the finger.
      const col = Math.round(px / w);
      if (col !== lastCol) {
        lastCol = col;
        hapticTap();
        navRef.current(TABS[col].to);
      }
    };

    const onEnd = () => {
      if (!tracking) return;
      tracking = false;
      if (!engaged) return;
      engaged = false;
      draggingRef.current = false;
      glide.classList.remove("tab-glide-held");
      // Hand the glass back to CSS: transition on, snap to the column the
      // route already landed on during the drag.
      glide.style.transition = "";
      glide.style.transform = `translateX(${Math.max(0, activeRef.current) * 100}%) scaleX(1)`;
    };

    row.addEventListener("touchstart", onStart, { passive: true });
    row.addEventListener("touchmove", onMove, { passive: false });
    row.addEventListener("touchend", onEnd);
    row.addEventListener("touchcancel", onEnd);
    return () => {
      row.removeEventListener("touchstart", onStart);
      row.removeEventListener("touchmove", onMove);
      row.removeEventListener("touchend", onEnd);
      row.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  // The page swipe tugs the glass toward the tab it is heading for, so the
  // content and the bar move as one machine. useTabSwipe reports progress as
  // a ratio in [-1, 1]; the glass leans up to 40% of a column that way, then
  // either follows the committed navigation or eases home on cancel.
  useEffect(() => {
    const glide = glideRef.current;
    if (!glide) return;
    const onSwipe = (e: Event) => {
      if (draggingRef.current) return; // a finger on the bar outranks the page
      const { phase, ratio } = (e as CustomEvent<{ phase: "move" | "end"; ratio?: number }>).detail;
      if (phase === "move" && typeof ratio === "number") {
        const base = Math.max(0, activeRef.current) * 100;
        glide.style.transition = "none";
        // Content moving left (negative ratio) reveals the NEXT tab — the
        // glass leans right, toward where the page is going.
        glide.style.transform = `translateX(calc(${base}% + ${-ratio * 40}%)) scaleX(1.03)`;
      } else {
        glide.style.transition = "";
        glide.style.transform = `translateX(${Math.max(0, activeRef.current) * 100}%) scaleX(1)`;
      }
    };
    window.addEventListener("caloly:tabswipe", onSwipe);
    return () => window.removeEventListener("caloly:tabswipe", onSwipe);
  }, []);

  /**
   * Tap = chosen the moment the finger lands (see the touch-latency fix):
   * a click only fires once the browser has ruled out every other gesture,
   * and on iOS a touch landing mid-momentum is spent stopping the scroll.
   */
  const select = (to: string) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;          // right-click / middle-click stay alone
    e.preventDefault();                  // no click needed, and no focus flash
    hapticTap();
    navigate(to);
  };

  return (
    <nav className="tab-dock fixed bottom-0 inset-x-0 z-40">
      <div ref={rowRef} className="relative flex pb-safe">
        <span
          ref={glideRef}
          aria-hidden="true"
          className="tab-glide"
          style={{ width: `${100 / TABS.length}%` }}
        />
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === "/"}
            onPointerDown={select(tab.to)}
            // Keyboard and assistive tech still activate the link normally;
            // navigating to the route we are already on is a no-op.
            onClick={hapticTap}
            className={({ isActive }) =>
              // Selected is brand green, icon and label together, on the lit
              // glass. Inactive is muted rather than faint: faint is the
              // ornament token, legible alone but reading as absent beside a
              // lit neighbour.
              // px-[4px], not px-1: rem padding grows with Dynamic Type and
              // eats the very width the label needs.
              `tab-hit relative flex-1 min-w-0 flex flex-col items-center gap-1 px-[4px] pt-[14px] pb-2 font-medium tracking-wide transition-colors press ${
                isActive ? "text-brand" : "text-fg-muted"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <tab.Icon
                  size={21}
                  strokeWidth={isActive ? 2.6 : 2.2}
                  aria-hidden="true"
                  // Cal keeps its colours; dimming is what says "not selected".
                  className={tab.Icon === CalAIMark && !isActive ? "opacity-55 saturate-50" : ""}
                />
                <span className="tab-label w-full text-center">{t(tab.key)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
